import { useState, useCallback, useRef } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Slider } from "../ui/slider";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ScrollArea } from "../ui/scroll-area";
import {
  Droplet,
  Stamp,
  Hash,
  LayoutGrid,
  Layers,
  FileText,
  Upload,
  Plus,
  X,
  Check,
  Loader2,
  Type,
  Image as ImageIcon,
  Calendar,
  User,
  Star,
  CheckCircle,
  AlertCircle,
  Scissors,
  Move,
  RotateCcw,
  ZoomIn,
  ZoomOut
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";

/**
 * AdvancedToolsDialog - Outils avancés ré-éditables
 * Phase D: Filigranes, Tampons, Numérotation, N-up, Superposition, Composer PDF
 */

// Preset watermarks
const WATERMARK_PRESETS = [
  { id: "draft", text: "BROUILLON", color: "#94a3b8", opacity: 0.3 },
  { id: "confidentiel", text: "CONFIDENTIEL", color: "#ef4444", opacity: 0.2 },
  { id: "sample", text: "EXEMPLE", color: "#3b82f6", opacity: 0.25 },
  { id: "correction", text: "CORRECTION", color: "#10b981", opacity: 0.3 },
  { id: "custom", text: "Texte personnalisé", color: "#64748b", opacity: 0.3 },
];

// Preset stamps
const STAMP_PRESETS = [
  { id: "bravo", icon: "⭐", text: "Bravo !", color: "#fbbf24", bgColor: "#fef3c7" },
  { id: "excellent", icon: "🌟", text: "Excellent", color: "#10b981", bgColor: "#dcfce7" },
  { id: "courage", icon: "💪", text: "Courage !", color: "#3b82f6", bgColor: "#dbeafe" },
  { id: "revoir", icon: "📝", text: "À revoir", color: "#f59e0b", bgColor: "#fef3c7" },
  { id: "vu", icon: "✓", text: "Vu", color: "#10b981", bgColor: "#dcfce7" },
  { id: "date", icon: "📅", text: "Date", color: "#6366f1", bgColor: "#e0e7ff", isDate: true },
  { id: "signature", icon: "✍️", text: "Signature", color: "#8b5cf6", bgColor: "#ede9fe", isSignature: true },
];

// Page number positions
const PAGE_NUMBER_POSITIONS = [
  { id: "bottom-center", label: "Bas centre", x: "center", y: "bottom" },
  { id: "bottom-right", label: "Bas droite", x: "right", y: "bottom" },
  { id: "bottom-left", label: "Bas gauche", x: "left", y: "bottom" },
  { id: "top-center", label: "Haut centre", x: "center", y: "top" },
  { id: "top-right", label: "Haut droite", x: "right", y: "top" },
  { id: "top-left", label: "Haut gauche", x: "left", y: "top" },
];

// Page number formats
const PAGE_NUMBER_FORMATS = [
  { id: "simple", label: "1", example: "1" },
  { id: "of-total", label: "1/5", example: "1/5" },
  { id: "page-x", label: "Page 1", example: "Page 1" },
  { id: "page-x-of-y", label: "Page 1 sur 5", example: "Page 1 sur 5" },
  { id: "dash", label: "- 1 -", example: "- 1 -" },
];

// N-up layouts
const NUP_LAYOUTS = [
  { id: "2x1", cols: 2, rows: 1, label: "2 par page (horizontal)" },
  { id: "1x2", cols: 1, rows: 2, label: "2 par page (vertical)" },
  { id: "2x2", cols: 2, rows: 2, label: "4 par page" },
  { id: "3x2", cols: 3, rows: 2, label: "6 par page" },
  { id: "3x3", cols: 3, rows: 3, label: "9 par page" },
  { id: "4x4", cols: 4, rows: 4, label: "16 par page" },
];

export function AdvancedToolsDialog({ 
  open, 
  onClose, 
  onAddWatermark,
  onAddStamp,
  onAddPageNumbers,
  onApplyNup,
  onAddOverlay,
  pages = [],
  currentPageIndex = 0
}) {
  const [activeTab, setActiveTab] = useState("watermark");
  
  // Watermark state
  const [watermarkText, setWatermarkText] = useState("BROUILLON");
  const [watermarkColor, setWatermarkColor] = useState("#94a3b8");
  const [watermarkOpacity, setWatermarkOpacity] = useState(30);
  const [watermarkRotation, setWatermarkRotation] = useState(-30);
  const [watermarkFontSize, setWatermarkFontSize] = useState(48);
  
  // Stamp state
  const [selectedStamp, setSelectedStamp] = useState(STAMP_PRESETS[0]);
  const [stampText, setStampText] = useState("Bravo !");
  
  // Page number state
  const [pageNumberPosition, setPageNumberPosition] = useState("bottom-center");
  const [pageNumberFormat, setPageNumberFormat] = useState("simple");
  const [pageNumberStart, setPageNumberStart] = useState(1);
  
  // N-up state
  const [nupLayout, setNupLayout] = useState("2x2");
  const [selectedPages, setSelectedPages] = useState([]);
  
  // Handle watermark add
  const handleAddWatermark = () => {
    onAddWatermark?.({
      id: uuidv4(),
      type: "watermark",
      text: watermarkText,
      color: watermarkColor,
      opacity: watermarkOpacity / 100,
      rotation: watermarkRotation,
      fontSize: watermarkFontSize,
      x: "center",
      y: "center",
    });
    onClose();
  };
  
  // Handle stamp add
  const handleAddStamp = () => {
    const stamp = {
      id: uuidv4(),
      type: "stamp",
      icon: selectedStamp.icon,
      text: selectedStamp.isDate ? new Date().toLocaleDateString("fr-FR") : stampText,
      color: selectedStamp.color,
      bgColor: selectedStamp.bgColor,
      isSignature: selectedStamp.isSignature,
    };
    onAddStamp?.(stamp);
    onClose();
  };
  
  // Handle page numbers
  const handleAddPageNumbers = () => {
    onAddPageNumbers?.({
      position: pageNumberPosition,
      format: pageNumberFormat,
      startNumber: pageNumberStart,
    });
    onClose();
  };
  
  // Handle N-up
  const handleApplyNup = () => {
    const layout = NUP_LAYOUTS.find(l => l.id === nupLayout);
    onApplyNup?.({
      layout,
      pages: selectedPages.length > 0 ? selectedPages : pages.map((_, i) => i),
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-500" />
            Outils avancés
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-5 mb-4">
            <TabsTrigger value="watermark" className="text-xs">
              <Droplet className="w-4 h-4 mr-1" />
              Filigrane
            </TabsTrigger>
            <TabsTrigger value="stamp" className="text-xs">
              <Stamp className="w-4 h-4 mr-1" />
              Tampon
            </TabsTrigger>
            <TabsTrigger value="pagenumber" className="text-xs">
              <Hash className="w-4 h-4 mr-1" />
              N° page
            </TabsTrigger>
            <TabsTrigger value="nup" className="text-xs">
              <LayoutGrid className="w-4 h-4 mr-1" />
              N-up
            </TabsTrigger>
            <TabsTrigger value="overlay" className="text-xs">
              <Layers className="w-4 h-4 mr-1" />
              Calques
            </TabsTrigger>
          </TabsList>

          {/* Watermark Tab */}
          <TabsContent value="watermark" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Preview */}
              <div className="relative h-48 bg-slate-100 rounded-xl flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-white m-4 rounded-lg shadow-sm flex items-center justify-center">
                  <span 
                    className="font-bold select-none whitespace-nowrap"
                    style={{
                      color: watermarkColor,
                      opacity: watermarkOpacity / 100,
                      transform: `rotate(${watermarkRotation}deg)`,
                      fontSize: `${watermarkFontSize / 3}px`,
                    }}
                  >
                    {watermarkText}
                  </span>
                </div>
              </div>

              {/* Settings */}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Texte</Label>
                  <Input
                    value={watermarkText}
                    onChange={(e) => setWatermarkText(e.target.value)}
                    placeholder="Texte du filigrane"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs">Préréglages</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {WATERMARK_PRESETS.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => {
                          setWatermarkText(preset.text);
                          setWatermarkColor(preset.color);
                          setWatermarkOpacity(preset.opacity * 100);
                        }}
                        className={`px-2 py-1 text-xs rounded-full transition-colors ${
                          watermarkText === preset.text
                            ? "bg-indigo-100 text-indigo-700"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {preset.text}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Couleur</Label>
                  <div className="flex gap-2 mt-1">
                    {["#94a3b8", "#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"].map((color) => (
                      <button
                        key={color}
                        onClick={() => setWatermarkColor(color)}
                        className={`w-7 h-7 rounded-full border-2 ${
                          watermarkColor === color ? "border-indigo-500 scale-110" : "border-transparent"
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Opacité ({watermarkOpacity}%)</Label>
                  <Slider
                    value={[watermarkOpacity]}
                    onValueChange={([v]) => setWatermarkOpacity(v)}
                    min={10}
                    max={80}
                    step={5}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label className="text-xs">Rotation ({watermarkRotation}°)</Label>
                  <Slider
                    value={[watermarkRotation]}
                    onValueChange={([v]) => setWatermarkRotation(v)}
                    min={-90}
                    max={90}
                    step={15}
                    className="mt-2"
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={handleAddWatermark}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter le filigrane
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* Stamp Tab */}
          <TabsContent value="stamp" className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              {STAMP_PRESETS.map((stamp) => (
                <button
                  key={stamp.id}
                  onClick={() => {
                    setSelectedStamp(stamp);
                    setStampText(stamp.text);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    selectedStamp.id === stamp.id
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <span className="text-2xl">{stamp.icon}</span>
                  <p className="text-xs font-medium mt-1" style={{ color: stamp.color }}>
                    {stamp.text}
                  </p>
                </button>
              ))}
            </div>

            {!selectedStamp.isDate && !selectedStamp.isSignature && (
              <div>
                <Label className="text-xs">Texte personnalisé</Label>
                <Input
                  value={stampText}
                  onChange={(e) => setStampText(e.target.value)}
                  placeholder="Texte du tampon"
                  className="mt-1"
                />
              </div>
            )}

            {/* Preview */}
            <div className="flex justify-center">
              <div 
                className="px-6 py-3 rounded-xl font-bold text-lg flex items-center gap-2"
                style={{ 
                  backgroundColor: selectedStamp.bgColor,
                  color: selectedStamp.color,
                  border: `2px solid ${selectedStamp.color}`
                }}
              >
                <span className="text-2xl">{selectedStamp.icon}</span>
                {selectedStamp.isDate 
                  ? new Date().toLocaleDateString("fr-FR")
                  : selectedStamp.isSignature 
                    ? "________________" 
                    : stampText
                }
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={handleAddStamp}>
                <Stamp className="w-4 h-4 mr-2" />
                Ajouter le tampon
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* Page Number Tab */}
          <TabsContent value="pagenumber" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Position */}
              <div>
                <Label className="text-xs mb-2 block">Position</Label>
                <div className="grid grid-cols-3 gap-2">
                  {PAGE_NUMBER_POSITIONS.map((pos) => (
                    <button
                      key={pos.id}
                      onClick={() => setPageNumberPosition(pos.id)}
                      className={`p-2 text-xs rounded-lg border-2 transition-colors ${
                        pageNumberPosition === pos.id
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {pos.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Format */}
              <div>
                <Label className="text-xs mb-2 block">Format</Label>
                <div className="space-y-2">
                  {PAGE_NUMBER_FORMATS.map((format) => (
                    <button
                      key={format.id}
                      onClick={() => setPageNumberFormat(format.id)}
                      className={`w-full p-2 text-sm rounded-lg border-2 text-left flex justify-between items-center transition-colors ${
                        pageNumberFormat === format.id
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <span>{format.label}</span>
                      <span className="text-slate-400">{format.example}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Commencer à partir de</Label>
              <Input
                type="number"
                value={pageNumberStart}
                onChange={(e) => setPageNumberStart(parseInt(e.target.value) || 1)}
                min={1}
                className="w-24 mt-1"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={handleAddPageNumbers}>
                <Hash className="w-4 h-4 mr-2" />
                Appliquer la numérotation
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* N-up Tab */}
          <TabsContent value="nup" className="space-y-4">
            <p className="text-sm text-slate-600">
              Combinez plusieurs pages sur une seule feuille pour l'impression.
            </p>

            <div className="grid grid-cols-3 gap-3">
              {NUP_LAYOUTS.map((layout) => (
                <button
                  key={layout.id}
                  onClick={() => setNupLayout(layout.id)}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    nupLayout === layout.id
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {/* Grid preview */}
                  <div 
                    className="w-full aspect-[3/4] bg-slate-100 rounded-lg mb-2 grid gap-0.5 p-1"
                    style={{ 
                      gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
                      gridTemplateRows: `repeat(${layout.rows}, 1fr)`,
                    }}
                  >
                    {Array.from({ length: layout.cols * layout.rows }).map((_, i) => (
                      <div key={i} className="bg-white rounded-sm" />
                    ))}
                  </div>
                  <p className="text-xs font-medium text-center">{layout.label}</p>
                </button>
              ))}
            </div>

            {pages.length > 1 && (
              <div>
                <Label className="text-xs mb-2 block">Pages à inclure ({pages.length} disponibles)</Label>
                <div className="flex flex-wrap gap-2">
                  {pages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        if (selectedPages.includes(i)) {
                          setSelectedPages(selectedPages.filter(p => p !== i));
                        } else {
                          setSelectedPages([...selectedPages, i]);
                        }
                      }}
                      className={`w-10 h-10 rounded-lg border-2 font-medium transition-colors ${
                        selectedPages.includes(i) || selectedPages.length === 0
                          ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {selectedPages.length === 0 
                    ? "Toutes les pages seront incluses"
                    : `${selectedPages.length} page(s) sélectionnée(s)`
                  }
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annuler</Button>
              <Button onClick={handleApplyNup}>
                <LayoutGrid className="w-4 h-4 mr-2" />
                Créer le N-up
              </Button>
            </DialogFooter>
          </TabsContent>

          {/* Overlay Tab */}
          <TabsContent value="overlay" className="space-y-4">
            <p className="text-sm text-slate-600">
              Superposez des éléments (texte, images) sur toutes les pages ou une sélection.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => onAddOverlay?.({ type: "text" })}
                className="p-6 rounded-xl border-2 border-slate-200 hover:border-indigo-400 transition-colors text-center"
              >
                <Type className="w-8 h-8 mx-auto mb-2 text-indigo-500" />
                <p className="font-medium">Texte en superposition</p>
                <p className="text-xs text-slate-500 mt-1">
                  Titre, entête, mention légale...
                </p>
              </button>

              <button
                onClick={() => onAddOverlay?.({ type: "image" })}
                className="p-6 rounded-xl border-2 border-slate-200 hover:border-indigo-400 transition-colors text-center"
              >
                <ImageIcon className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                <p className="font-medium">Image en superposition</p>
                <p className="text-xs text-slate-500 mt-1">
                  Logo, illustration, décoration...
                </p>
              </button>
            </div>

            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Conseil</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Les éléments en superposition apparaîtront au-dessus du contenu existant. 
                    Utilisez l'opacité pour créer des effets de transparence.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export default AdvancedToolsDialog;
